import {
    Avatar,
    Colors,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import { EVALUATION_PANEL_INNER_SURFACE } from './EvaluationPanel';
import { EvaluationProgressBar } from './EvaluationProgressBar';

import type { User } from '@/types/directory/User';
import type { Evaluation360Entry } from '@/types/performance-profile';

import { TileLink } from '@/components/TileLink';
import { evaluationDestination } from '@/navigation/evaluationDestination';
import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

/** How many reviewers are shown as avatars; the rest are named in the tooltip beside them. */
const SHOWN_REVIEWERS = 3;

interface Evaluation360CardProps {
    entry: Evaluation360Entry;
    /** The reviewers the panel has already resolved; a missing one shows as an empty avatar. */
    reviewerUsers: User[] | undefined;
}

/**
 * One 360 feedback of the profile: its date, its questionnaire, who reviews it and how far along
 * it is. The whole card leads to that evaluation's results.
 */
export const Evaluation360Card = ({ entry, reviewerUsers }: Evaluation360CardProps) => {
    const { t } = useTranslation();
    const findReviewer = (reviewerId: string) => reviewerUsers?.find((u) => u.id === reviewerId);
    const hiddenReviewers = entry.reviewerUserIds
        .slice(SHOWN_REVIEWERS)
        .map(findReviewer)
        .filter((user): user is User => Boolean(user));

    return (
        <TileLink
            to={evaluationDestination({
                reviewInstanceId: entry.reviewInstanceId,
                cycleInstanceId: entry.cycleInstanceId,
            })}
            ariaLabel={t('performanceProfile.evaluations.evaluation360.openLabel', {
                date: entry.date,
                name: entry.evaluationName,
            })}
            className={`${EVALUATION_PANEL_INNER_SURFACE} block`}
        >
            <div className={'flex items-start justify-between'}>
                <div className={'flex flex-col gap-2'}>
                    <span className={'text-md text-navy font-medium'}>{entry.date}</span>
                    <span className={'text-navy text-[14px] font-semibold'}>
                        {entry.evaluationName}
                    </span>
                </div>
                <div className={'flex items-center gap-1'}>
                    <div className={'flex -space-x-2'}>
                        {entry.reviewerUserIds.slice(0, SHOWN_REVIEWERS).map((reviewerId) => {
                            const user = findReviewer(reviewerId);
                            const name = getFullName(user);

                            // The library avatar: the reviewer's picture when there is one,
                            // otherwise dark initials on a pale disc -- white initials on grey
                            // read at about 1.5:1.
                            return (
                                <Avatar
                                    key={reviewerId}
                                    alt={name}
                                    initials={getInitials(name) || undefined}
                                    src={getProfilePictureUrl(user?.profilePictureUrl) || undefined}
                                    size={'3xs'}
                                    borderColor={Colors.WHITE}
                                    borderSize={'ring-2'}
                                />
                            );
                        })}
                    </div>
                    {hiddenReviewers.length > 0 && (
                        <TooltipProvider>
                            <Tooltip delayDuration={200}>
                                <TooltipTrigger asChild>
                                    {/* Nested in the card's link: naming the other reviewers is a
                                        second action, and its click must not open the evaluation. */}
                                    <div
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                        }}
                                        className={
                                            'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-2 border-white bg-(--color-primary-800) text-sm text-white'
                                        }
                                    >
                                        +{hiddenReviewers.length}
                                    </div>
                                </TooltipTrigger>

                                <TooltipContent
                                    className={'pointer-events-auto max-w-xs'}
                                    side={'right'}
                                    layout={'list'}
                                >
                                    <div className={'space-y-2 p-2'}>
                                        {hiddenReviewers.map((user) => (
                                            <div
                                                key={user.id}
                                                className={'flex flex-row items-center'}
                                            >
                                                <Avatar
                                                    alt={getFullName(user)}
                                                    initials={getInitials(user)}
                                                    src={getProfilePictureUrl(
                                                        user.profilePictureUrl || '',
                                                    )}
                                                    className={'bg-base-50 mr-2'}
                                                    size={'4xs'}
                                                />

                                                <Text size={'small'}>{getFullName(user)}</Text>
                                            </div>
                                        ))}
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
            </div>
            <div className={'mt-3'}>
                <div className={'mb-1 flex items-center justify-between'}>
                    <span className={'text-obj-gray text-xs'}>
                        {t('performanceProfile.evaluations.evaluation360.completion')}
                    </span>
                    <span className={'text-primary text-xs font-semibold'}>
                        {entry.completionPercentage}%
                    </span>
                </div>
                <EvaluationProgressBar percentage={entry.completionPercentage} />
            </div>
        </TileLink>
    );
};
